import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import useBem from '@indicia/use-bem';

import BackgroundContainer from '../../shared/components/background-container/background-container';
import GLTBreadcrumb from '../../shared/components/glt-breadcrumb/GLTBreadcrumb';
import LoaderContainer from '../../shared/components/loader/LoaderContainer';
import Events from '../../shared/utils/events';
import ThemeEmployerHelp from './components/theme-employer-help/theme-employer-help';
import ThemeFoldoutHeader from './components/theme-foldout-header/theme-foldout-header';
import ThemeHeader from './components/theme-header/theme-header';
import ThemeTips from './components/theme-tips/theme-tips';
import ThemeZkHelp from './components/theme-zk-help/theme-zk-help';
import useTheme from './hooks/use-theme';

import './theme-page.scss';

const ThemePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { theme } = useParams();
  const [URLSearchParams] = useSearchParams();
  const [language, setLanguage] = useState(() => localStorage.getItem('i18nextLng') ?? 'nl');

  const updateLanguage = () => {
    const newLanguage = localStorage.getItem('i18nextLng');
    if (newLanguage === null || newLanguage === 'null') {
      setLanguage('nl');
    } else {
      setLanguage(newLanguage);
    }
  };

  if (URLSearchParams.get('activationCode')) {
    sessionStorage.setItem('gezondleven-activation-code', URLSearchParams.get('activationCode')!);
  }

  useEffect(() => {
    window.addEventListener(Events.LOCAL_STORAGE_UPDATE_LANGUAGE, () => updateLanguage());
    return () => {
      window.removeEventListener(Events.LOCAL_STORAGE_UPDATE_LANGUAGE, () => updateLanguage());
    };
  }, []);

  const key = sessionStorage.getItem('gezondleven-activation-code');
  const { bemClassName } = useBem('theme-page');

  const { data, isLoading } = useTheme(key ?? null, theme!, language);
  const [foldoutActive, setFoldoutActive] = useState<boolean>(false);
  if (!key || !theme) {
    navigate('/');
  }

  if (isLoading) {
    return <LoaderContainer text={t('theme.loading')} />;
  }

  const { showProductsImmediately, header, tipsContainer, employerSolutions, insurerHeader, offeredSolutions } = data!;

  return (
    <div className={bemClassName()}>
      <div className={bemClassName('breadcrumb')}>
        <GLTBreadcrumb className={bemClassName('breadcrumb-inner')} />
      </div>
      <ThemeHeader {...header} />
      <div className={bemClassName('content')}>
        <ThemeTips {...tipsContainer} />
        {employerSolutions && employerSolutions.solutions.length > 0 ? (
          <ThemeEmployerHelp {...employerSolutions} />
        ) : (
          <div className={bemClassName('empty')} />
        )}
        {showProductsImmediately && (
          <ThemeZkHelp
            {...offeredSolutions}
            showProductsImmediately={showProductsImmediately}
          />
        )}
        {!showProductsImmediately && (
          <>
            <ThemeFoldoutHeader
              {...insurerHeader}
              onClick={() => setFoldoutActive(!foldoutActive)}
              active={foldoutActive}
            />
            <motion.div
              className={bemClassName('foldout')}
              initial={{ height: 0, overflow: 'hidden' }}
              animate={{ height: foldoutActive ? 'auto' : 0, overflow: foldoutActive ? 'visible' : 'hidden' }}
              transition={{ duration: 0.3 }}
              onUpdate={() => {
                if (foldoutActive) {
                  document
                    .querySelector(`.${bemClassName('foldout')}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
                }
              }}
            >
              <ThemeZkHelp
                {...offeredSolutions}
                showProductsImmediately={showProductsImmediately}
              />
            </motion.div>
          </>
        )}
      </div>
      <BackgroundContainer type="white">
        <div className="container">
          <div className={bemClassName('footer')}>{t('theme.footer')}</div>
        </div>
      </BackgroundContainer>
    </div>
  );
};

export default ThemePage;
